import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import { Plus, ClipboardCheck, AlertTriangle, CheckCircle2, Clock, ChevronRight, Sparkles as CleanIcon, User } from "lucide-react";

const STATUS_META = {
  assigned:                       { label: "Assigned",                cls: "bg-[color:var(--gold)]/10 text-[color:var(--gold)] border-[color:var(--gold)]/40" },
  in_progress:                    { label: "In Progress",             cls: "bg-blue-500/10 text-blue-300 border-blue-400/40" },
  submitted:                      { label: "Waiting GM Approval",     cls: "bg-purple-500/10 text-purple-300 border-purple-400/40" },
  resubmitted_to_gm:              { label: "Resubmitted To GM",       cls: "bg-purple-500/10 text-purple-300 border-purple-400/40" },
  awaiting_global_review:         { label: "Awaiting Global Review",  cls: "bg-indigo-500/10 text-indigo-300 border-indigo-400/40" },
  returned_to_gm:                 { label: "Returned To GM",          cls: "bg-[color:var(--warning)]/10 text-[color:var(--warning)] border-[color:var(--warning)]/40" },
  correction_required:            { label: "Correction Required",     cls: "bg-[color:var(--warning)]/10 text-[color:var(--warning)] border-[color:var(--warning)]/40" },
  completed:                      { label: "Final Approved",          cls: "bg-emerald-500/10 text-emerald-300 border-emerald-400/40" },
};

export default function DisplayDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canAssign = hasAccess(user?.role, ["admin", "gm"]);
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [drill, setDrill] = useState(null);

  const load = useCallback(() => {
    api.get("/display_checks").then(({ data }) => setChecks(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/display_dashboard").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filter === "all" ? checks : checks.filter((c) => c.status === filter), [checks, filter]);

  const startBadge = (c) => STATUS_META[c.status] || { label: c.status, cls: "" };
  const zoneCount = (c) => (c.zones || []).length;
  const doneCount = (c) => (c.zones || []).filter((z) => ["completed", "corrected"].includes(z.status)).length;

  return (
    <div className="space-y-6" data-testid="display-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Visual Merchandising</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Display Checks</h1>
        </div>
        {canAssign && (
          <div className="flex gap-2">
            <Link to="/display/zone-owner-history" className="hos-btn-outline flex items-center gap-2 text-sm" data-testid="zoh-link"><User size={14}/> Zone Owner History</Link>
            <Link to="/display/reports" className="hos-btn-outline flex items-center gap-2 text-sm" data-testid="reports-link">Reports</Link>
            <Link to="/display/photos" className="hos-btn-outline flex items-center gap-2 text-sm" data-testid="photos-link">Photos</Link>
            <Link to="/display/new" className="hos-btn-primary flex items-center gap-2" data-testid="new-dc-btn">
              <Plus size={16}/> New Display Check
            </Link>
          </div>
        )}
        {!canAssign && (
          <div className="flex gap-2">
            <Link to="/display/reports" className="hos-btn-outline flex items-center gap-2 text-sm" data-testid="reports-link">Reports</Link>
            <Link to="/display/photos" className="hos-btn-outline flex items-center gap-2 text-sm" data-testid="photos-link">Photos</Link>
          </div>
        )}
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard icon={ClipboardCheck} k="Assigned" v={stats.by_status?.assigned || 0} />
            <StatCard icon={Clock} k="In Progress" v={stats.by_status?.in_progress || 0} />
            <StatCard icon={ChevronRight} k="Waiting Approval" v={stats.by_status?.submitted || 0} />
            <StatCard icon={AlertTriangle} k="Corrections" v={stats.by_status?.correction_required || 0} accent="warning"/>
            <StatCard icon={CheckCircle2} k="Completed" v={stats.by_status?.completed || 0} accent="success"/>
            <div className="hos-card p-4 col-span-2 md:col-span-5 flex items-center justify-between">
              <div>
                <div className="label-caps">Display Compliance</div>
                <div className="font-heading text-3xl text-[color:var(--gold)]">{stats.compliance_pct}%</div>
              </div>
              {stats.critical_open > 0 && (
                <div className="px-3 py-2 rounded-md border border-[color:var(--error)] text-[color:var(--error)] flex items-center gap-2">
                  <AlertTriangle size={16}/> {stats.critical_open} critical open
                </div>
              )}
            </div>
          </div>

          {/* Cleanliness dashboard cards (last 30 days) */}
          <div>
            <div className="label-caps mb-2 flex items-center gap-2"><CleanIcon size={14}/> Cleanliness (last 30 days)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={CheckCircle2} k="Clean Zones" v={(stats.cleanliness?.very_clean || 0) + (stats.cleanliness?.clean || 0)} accent="success"/>
              <StatCard icon={AlertTriangle} k="Needs Cleaning" v={(stats.cleanliness?.needs_cleaning || 0) + (stats.cleanliness?.dirty || 0) + (stats.cleanliness?.critical || 0)} accent="warning"
                onClick={() => setDrill("clean")}/>
              <StatCard icon={AlertTriangle} k="Zones With Dust" v={(stats.dust?.light || 0) + (stats.dust?.moderate || 0) + (stats.dust?.heavy || 0)} accent="warning"
                onClick={() => setDrill("dust")}/>
              <StatCard icon={AlertTriangle} k="Critical Cleaning" v={stats.cleanliness?.critical || 0} accent="error"/>
            </div>
            {drill === "clean" && (stats.needs_cleaning_zones || []).length > 0 && (
              <CleanDrill title="Zones Needing Cleaning" items={stats.needs_cleaning_zones} labelKey="cleanliness_status" onClose={() => setDrill(null)}/>
            )}
            {drill === "dust" && (stats.dusty_zones || []).length > 0 && (
              <CleanDrill title="Zones With Dust" items={stats.dusty_zones} labelKey="dust_condition" onClose={() => setDrill(null)}/>
            )}
          </div>
        </>
      )}

      <div className="flex flex-wrap gap-2">
        {["all", "assigned", "in_progress", "submitted", "awaiting_global_review", "returned_to_gm", "correction_required", "completed"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 text-xs rounded-full border ${filter === k ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
            data-testid={`filter-${k}`}
          >
            {k === "all" ? "All" : STATUS_META[k]?.label || k}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map((c) => {
          const b = startBadge(c);
          const pct = zoneCount(c) ? Math.round(100 * doneCount(c) / zoneCount(c)) : 0;
          const target = hasAccess(user?.role, ["admin", "gm", "global_standards"]) || c.status === "completed" ? `/display/${c.id}` : `/display/${c.id}/run`;
          return (
            <button
              key={c.id}
              onClick={() => nav(target)}
              className="hos-card p-4 text-left hover:border-[color:var(--gold)] transition"
              data-testid={`dc-card-${c.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-heading text-lg truncate">{c.check_number}</div>
                <span className={`text-[10px] px-2 py-1 rounded-full border ${b.cls}`}>{b.label}</span>
              </div>
              <div className="text-sm text-[color:var(--text-secondary)] mt-1">{c.store_name} · {c.inspection_date}</div>
              <div className="text-sm mt-1">Manager: <span className="text-[color:var(--gold)]">{c.manager_name}</span></div>
              <div className="text-xs text-[color:var(--text-secondary)] mt-2">{doneCount(c)}/{zoneCount(c)} zones inspected</div>
              <div className="mt-2 h-1.5 bg-[color:var(--bg-surface-2)] rounded-full overflow-hidden">
                <div className="h-full bg-[color:var(--gold)]" style={{ width: `${pct}%` }}/>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="hos-card p-8 text-center text-[color:var(--text-secondary)] col-span-full">
            No display checks yet. {canAssign && "Click 'New Display Check' to create one."}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, k, v, accent, onClick }) {
  const color = accent === "warning" ? "text-[color:var(--warning)]" : accent === "success" ? "text-emerald-400" : accent === "error" ? "text-[color:var(--error)]" : "text-[color:var(--gold)]";
  const cls = `hos-card p-4 ${onClick ? "cursor-pointer hover:border-[color:var(--gold)]" : ""}`;
  return (
    <div className={cls} onClick={onClick} data-testid={`stat-${k.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between">
        <div className="label-caps">{k}</div>
        <Icon size={16} className={color}/>
      </div>
      <div className={`font-heading text-3xl ${color}`}>{v}</div>
    </div>
  );
}

function CleanDrill({ title, items, labelKey, onClose }) {
  return (
    <div className="hos-card p-4 mt-3" data-testid="clean-drill">
      <div className="flex items-center justify-between mb-2">
        <div className="font-heading text-lg">{title}</div>
        <button className="text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--gold)]" onClick={onClose}>Close</button>
      </div>
      <ul className="divide-y divide-[color:var(--border)]">
        {items.map((z, i) => (
          <li key={i} className="py-2 text-sm flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Link to={`/display/${z.check_id}`} className="font-semibold hover:text-[color:var(--gold)]">{z.zone_name}</Link>
              <div className="text-[11px] text-[color:var(--text-secondary)]">
                {z.store_name} · {z.inspection_date} · <span className="capitalize">{(z[labelKey] || "—").replace(/_/g, " ")}</span>
              </div>
            </div>
            <Link to={`/display/${z.check_id}`} className="text-[color:var(--gold)] text-xs">Open</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
